import React from 'react';
import WikidataEntityDialog from '../../../../../components/prosemirror-editor/components/WikidataEntityDialog';
import WikidataEntitySearchDialog from '../../../../../components/prosemirror-editor/components/EntitySearchDialog';
import MentionHoverCard from '../../../../../components/entityMentions/MentionHoverCard';
import {getEntityKBUrl, isLocalEntityRef} from '../../../../../utils/entityLinks';
import {resolveEntityAvatarUrl} from '../../../../../utils/entityAvatarUrl';
import {linkDataItemEntity} from './sourcesApi';
import {fetchEntityTypesByQid} from '../../../../../utils/api/entities';
import {coalesceEntityType} from '../../../../../utils/entityTypeMeta';
import {
    shouldDisplayUnlinkedMentions,
    unlinkedMentionBorderStyle,
    useEntityDisplayConfig,
} from '../../../../../hooks/entityDisplayConfig';

/**
 * Shared entity-highlighting primitives used by both the audio transcript view
 * (SourcesTranscriptView) and the PDF extracted-text view (ReadonlyProseMirrorRenderer).
 *
 * Entity / mention highlighting mirrors the report exactly (no bespoke logic):
 * see `features/products/previewPage/InlineRenderer.js`. A `wikidataEntity` mark
 * that resolves to a real Wikidata QID (`/^Q\d+$/`) renders as a blue dotted-underline
 * clickable span (opens the WikidataEntityDialog); a recognised-but-unlinked mention
 * renders with a yellow underline.
 *
 * Unified mention behaviour (matching reports & corpus):
 * - HOVER on any mention shows a popup with name + type + confidence (MentionHoverCard).
 * - CLICK on a YELLOW (unlinked) mention opens the Wikidata search dialog DIRECTLY
 *   (no intermediate dialog) and, on selection, persists the link via the manual-link
 *   endpoint (DataItemLinkContext + linkDataItemEntity). Enabled only when the viewer
 *   provides a DataItemLinkContext value (referenceId + userId + enabled), so other
 *   consumers (report preview, transcripts without linking) keep "not clickable".
 * - CLICK on a BLUE (linked) entity opens the entity info popover (WikidataEntityDialog).
 */

/**
 * Context that enables manual Wikidata linking of unlinked mentions in the Data Items viewer.
 * `enabled` should be true only when the rendered doc is the English/enrichment doc (the only
 * doc whose mentions the backend can patch). When the value is null (default), unlinked mentions
 * are not clickable — preserving behaviour for non-data-item consumers.
 *
 * @type {React.Context<null | {referenceId: (string|number), userId: string, enabled: boolean}>}
 */
export const DataItemLinkContext = React.createContext(null);

/**
 * Context exposing a single, shared Wikidata search dialog for the current viewer.
 *
 * PERF: Previously every unlinked mention (`canManuallyLink`) mounted its own
 * `<WikidataEntitySearchDialog keepMounted>`. In an NER-annotated data-item doc with
 * hundreds of unlinked mentions this mounted hundreds of hidden MUI Dialogs (Paper +
 * Autocomplete + TextField + fieldset + a fresh Emotion <style> per instance), inflating
 * the DOM from ~9k to 30k–40k nodes and blocking the main thread on scroll. Mounting a
 * single dialog at the viewer level and opening it on demand keeps exactly one dialog in
 * the tree regardless of mention count.
 *
 * Value: `(request: {initialQuery: string, onSelect: (entity) => void}) => void`, or null
 * when no shared provider is present (mentions then fall back to being non-clickable).
 *
 * @type {React.Context<null | ((request: {initialQuery: string, onSelect: Function}) => void)>}
 */
export const WikidataSearchContext = React.createContext(null);

const QID_RE = /^Q\d+$/i;

// Aligned with the report editor's wikidataEntity mark styling (editor.css:
// `.wikidata-entity`, `.wikidata-entity--linked`, `.wikidata-entity--mention`):
//   base    → border-radius: 2px (no padding on the span)
//   linked  → blue text + 1px dashed blue underline
//   mention → yellow 2px solid underline, inherited color
// The unlinked cursor becomes a pointer when manual linking is available in this viewer.
function getEntityMentionStyle(isLinked, linkable = false, unlinkedUnderlineEnabled = true) {
    if (!isLinked) {
        return {
            color: 'inherit',
            backgroundColor: 'transparent',
            borderRadius: '2px',
            cursor: linkable ? 'pointer' : 'default',
            borderBottom: unlinkedMentionBorderStyle(unlinkedUnderlineEnabled),
            textDecoration: 'none',
        };
    }
    return {
        color: 'blue',
        backgroundColor: 'transparent',
        borderRadius: '2px',
        cursor: 'pointer',
        borderBottom: '1px dashed blue',
        textDecoration: 'none',
    };
}

/**
 * Paragraph-relative char offset of `spanEl` within its top-level `[data-block-index]` ancestor.
 * Mirrors the offset space the backend uses (concatenated inline text of the block), and matches
 * the selection-offset logic in ReadonlyProseMirrorRenderer. Returns null when the block can't be
 * resolved (e.g. mention sits outside a positional block) so the caller can no-op gracefully.
 */
function computeBlockOffset(spanEl) {
    if (!spanEl || typeof document === 'undefined') return null;
    let blockEl = spanEl;
    while (blockEl && !(blockEl.dataset && blockEl.dataset.blockIndex != null)) {
        blockEl = blockEl.parentElement;
    }
    if (!blockEl) return null;
    const blockIndex = Number(blockEl.dataset.blockIndex);
    if (!Number.isInteger(blockIndex)) return null;
    const range = document.createRange();
    range.selectNodeContents(blockEl);
    range.setEnd(spanEl, 0);
    const start = range.toString().length;
    return {blockIndex, start};
}

/**
 * Renders a single ProseMirror inline text node, applying marks.
 *
 * - bold / italic / code / strike → plain HTML wrappers
 * - link → <a> opening in new tab
 * - wikidataEntity → report-style entity/mention highlight (linked = blue dotted +
 *   clickable WikidataEntityDialog, unlinked = yellow underline, not clickable)
 */
export function InlineNode({node}) {
    const linkCtx = React.useContext(DataItemLinkContext);
    const openWikidataSearch = React.useContext(WikidataSearchContext);
    const {unlinkedUnderlineEnabled} = useEntityDisplayConfig();
    const [dialogAnchor, setDialogAnchor] = React.useState(null);
    const [linking, setLinking] = React.useState(false);
    // After a successful manual link we optimistically flip the span to linked using these attrs,
    // so it re-renders blue + clickable without waiting for the doc to refetch.
    const [linkedOverride, setLinkedOverride] = React.useState(null);
    const spanRef = React.useRef(null);
    // PERF: Do NOT store the DOM element in React state — calling setState in a ref callback on
    // every entity mention span causes a massive render storm when a large NER-annotated doc
    // mounts (thousands of simultaneous state updates → 35+ second main-thread block).
    // MentionHoverCard accepts a ref object and resolves .current lazily at hover time instead.

    if (!node || node.type !== 'text') return null;
    const text = node.text || '';
    const marks = Array.isArray(node.marks) ? node.marks : [];

    const wikidataEntityMark = marks.find(m => m.type === 'wikidataEntity');

    if (!wikidataEntityMark) {
        let result = <React.Fragment>{text}</React.Fragment>;
        for (const mark of marks) {
            if (mark.type === 'bold' || mark.type === 'strong') result = <strong>{result}</strong>;
            else if (mark.type === 'italic' || mark.type === 'em') result = <em>{result}</em>;
            else if (mark.type === 'code') result = <code>{result}</code>;
            else if (mark.type === 'strike') result = <s>{result}</s>;
            else if (mark.type === 'link') {
                const href = mark.attrs?.href || '#';
                result = <a href={href} target="_blank" rel="noopener noreferrer">{result}</a>;
            }
        }
        return result;
    }

    const baseAttrs = wikidataEntityMark.attrs || {};
    const attrs = linkedOverride ? {...baseAttrs, ...linkedOverride} : baseAttrs;
    // Schema stores the QID in `id`; older payloads may use `entityId`.
    const uid          = attrs.uid || null;
    const id          = attrs.id || attrs.entityId || null;
    const label       = attrs.label || (typeof id === 'string' ? id : '') || text;
    const url         = attrs.url || null;
    const description = attrs.description || null;
    const entityType  = attrs.entityType || null;
    const confidence  = attrs.confidence ?? null;
    const nerScore    = attrs.nerScore ?? confidence;
    const linkingScore = attrs.linkingScore ?? null;
    const isLinked    = typeof id === 'string' && (QID_RE.test(id.trim()) || isLocalEntityRef(id));
    const kbUrl       = isLinked ? getEntityKBUrl(id) : null;
    const imageUrl    = resolveEntityAvatarUrl({wikidataId: id, imageUrl: attrs.imageUrl || null});

    // Feature flag off: treat unlinked mentions as plain text (no underline, hover, or click).
    if (!isLinked && !shouldDisplayUnlinkedMentions(unlinkedUnderlineEnabled)) {
        let result = <React.Fragment>{text}</React.Fragment>;
        for (const mark of marks) {
            if (mark.type === 'wikidataEntity') continue;
            if (mark.type === 'bold' || mark.type === 'strong') result = <strong>{result}</strong>;
            else if (mark.type === 'italic' || mark.type === 'em') result = <em>{result}</em>;
            else if (mark.type === 'code') result = <code>{result}</code>;
            else if (mark.type === 'strike') result = <s>{result}</s>;
            else if (mark.type === 'link') {
                const href = mark.attrs?.href || '#';
                result = <a href={href} target="_blank" rel="noopener noreferrer">{result}</a>;
            }
        }
        return result;
    }

    // Manual linking is offered only for unlinked mentions when the viewer enabled it (English doc)
    // AND a shared entity search dialog is available (WikidataSearchContext provider present).
    const canManuallyLink = !isLinked
        && Boolean(linkCtx?.enabled && linkCtx?.referenceId && linkCtx?.userId)
        && typeof openWikidataSearch === 'function';

    const handleSelectEntity = async (entity) => {
        const entityRef = String(entity?.id || entity?.entityId || entity?.entityRef || '').trim();
        if (!entityRef || !canManuallyLink) return;
        const offset = computeBlockOffset(spanRef.current);
        if (!offset) {
            console.warn('[InlineNode] could not resolve block offset for manual link — skipping');
            return;
        }
        const {blockIndex, start} = offset;
        setLinking(true);
        try {
            const result = await linkDataItemEntity({
                referenceId: linkCtx.referenceId,
                userId: linkCtx.userId,
                pmBlockIndex: blockIndex,
                paragraphStartOffset: start,
                paragraphEndOffset: start + text.length,
                mentionText: text,
                wikidataEntity: {
                    id: entityRef,
                    label: entity?.label || entity?.canonicalName || null,
                    description: entity?.description || null,
                    entityType: coalesceEntityType(entity?.entityType, entityType) || null,
                    imageUrl: entity?.imageUrl || null,
                },
            });
            let resolvedEntityType = coalesceEntityType(
                result?.entityType,
                entity?.entityType,
                entityType,
            );
            if (!resolvedEntityType) {
                try {
                    const types = await fetchEntityTypesByQid([entityRef]);
                    resolvedEntityType = types[entityRef.trim().toUpperCase()] || null;
                } catch (_) {
                    // Best-effort.
                }
            }
            // Optimistically flip to linked (blue) using the server-confirmed attrs.
            setLinkedOverride({
                id: result?.id || entityRef,
                entityId: result?.id || entityRef,
                label: result?.label || entity?.label || entity?.canonicalName || text,
                description: result?.description || entity?.description || null,
                entityType: resolvedEntityType,
                imageUrl: result?.imageUrl || entity?.imageUrl || null,
                confidence: confidence,
            });
        } catch (err) {
            console.error('[InlineNode] manual link failed:', err);
        } finally {
            setLinking(false);
        }
    };

    const handleClick = (e) => {
        if (isLinked) {
            e.preventDefault();
            e.stopPropagation();
            setDialogAnchor(e.currentTarget);
            return;
        }
        if (!canManuallyLink || linking) return;
        // Yellow (unlinked) mention → open the shared Wikidata search dialog directly.
        e.preventDefault();
        e.stopPropagation();
        openWikidataSearch({initialQuery: text, onSelect: handleSelectEntity});
    };

    return (
        <>
            <MentionHoverCard
                name={label}
                entityType={entityType}
                entityRef={isLinked ? id : null}
                confidence={confidence}
                nerScore={nerScore}
                linkingScore={linkingScore}
                mentionAnchorRef={spanRef}
                mentionUid={uid}
                suppressHover={Boolean(dialogAnchor)}
            >
                <span
                    ref={spanRef}
                    onClick={handleClick}
                    style={getEntityMentionStyle(isLinked, canManuallyLink, unlinkedUnderlineEnabled)}
                    data-id={isLinked ? id : null}
                    data-wikidata-id={isLinked ? id : null}
                    data-wikidata-uid={uid || undefined}
                    data-url={kbUrl}
                >
                    {text}
                </span>
            </MentionHoverCard>

            {dialogAnchor && (
                <WikidataEntityDialog
                    open={true}
                    anchorEl={dialogAnchor}
                    id={id}
                    label={label}
                    url={url || kbUrl}
                    description={description}
                    entityType={entityType}
                    imageUrl={imageUrl}
                    onClose={() => setDialogAnchor(null)}
                />
            )}
        </>
    );
}

/**
 * Mounts exactly ONE WikidataEntitySearchDialog for the whole viewer subtree and provides an
 * imperative `openWikidataSearch({initialQuery, onSelect})` via WikidataSearchContext. Each
 * unlinked mention calls this on click instead of mounting its own dialog, so DOM size no longer
 * scales with mention count (see WikidataSearchContext docs for the perf rationale).
 */
export function WikidataSearchProvider({children}) {
    const [request, setRequest] = React.useState(null);
    const openWikidataSearch = React.useCallback((req) => {
        if (!req || typeof req.onSelect !== 'function') return;
        setRequest({initialQuery: req.initialQuery || '', onSelect: req.onSelect});
    }, []);

    const handleClose = React.useCallback(() => setRequest(null), []);
    const handleSelect = React.useCallback((entity) => {
        const cb = request?.onSelect;
        setRequest(null);
        if (typeof cb === 'function') cb(entity);
    }, [request]);

    return (
        <WikidataSearchContext.Provider value={openWikidataSearch}>
            {children}
            <WikidataEntitySearchDialog
                open={Boolean(request)}
                initialQuery={request?.initialQuery || ''}
                onClose={handleClose}
                onSelect={handleSelect}
            />
        </WikidataSearchContext.Provider>
    );
}
